// AcademicYearPanel — academic sessions (e.g. 2026-27) and year-end
// promotion: move a whole class up (untick anyone held back), or pass the
// final class out as alumni. Every move is kept in the student's class history.
import { useEffect, useState } from 'react';
import { Plus, ArrowRight, GraduationCap, Star, Users } from 'lucide-react';
import { getSessions, createSession, setCurrentSession, getClasses, getPromotion, promoteClass, getStudentsByStatus } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { fmtDate } from '../ui/format';

// Suggest the next April–March session after the latest one (or this one).
function suggestSession(sessions) {
  const now = new Date();
  const startYear = sessions.length
    ? Number(sessions[0].start_date.slice(0, 4)) + 1
    : (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1);
  return { name: `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`, start_date: `${startYear}-04-01`, end_date: `${startYear + 1}-03-31`, make_current: !sessions.length };
}

export default function AcademicYearPanel({ onOpenStudent }) {
  const [sessions, setSessions] = useState([]);
  const [form,     setForm]     = useState(null);
  const [classes,  setClasses]  = useState([]);
  const [fromId,   setFromId]   = useState('');
  const [toId,     setToId]     = useState('');
  const [data,     setData]     = useState(null);
  const [keep,     setKeep]     = useState(new Set());   // ids to promote (unticked = held back)
  const [keepRoll, setKeepRoll] = useState(false);
  const [alumni,   setAlumni]   = useState(null);
  const [msg,      setMsg]      = useState('');
  const [busy,     setBusy]     = useState(false);

  useEffect(() => { loadSessions(); getClasses().then(setClasses).catch(() => {}); }, []);
  useEffect(() => { if (fromId) loadClass(); else setData(null); }, [fromId]);

  async function loadSessions() {
    try { const s = await getSessions(); setSessions(s); setForm(f => f || (s.length ? null : suggestSession(s))); } catch (err) { setMsg(errText(err)); }
  }
  async function loadClass() {
    try {
      const d = await getPromotion(fromId);
      setData(d); setKeep(new Set(d.students.map(s => s.id)));
    } catch (err) { setMsg(errText(err)); }
  }

  async function saveSession(e) {
    e.preventDefault(); setMsg('');
    try { await createSession(form); setMsg(`${form.name} created ✅`); setForm(null); loadSessions(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function makeCurrent(s) {
    if (!window.confirm(`Make ${s.name} the current academic year?`)) return;
    try { await setCurrentSession(s.id); loadSessions(); if (fromId) loadClass(); } catch (err) { setMsg(errText(err)); }
  }

  async function promote() {
    const from = classes.find(c => String(c.id) === String(fromId))?.name;
    const to = toId === 'passout' ? null : classes.find(c => String(c.id) === String(toId))?.name;
    const held = data.students.length - keep.size;
    const text = to
      ? `Move ${keep.size} student${keep.size === 1 ? '' : 's'} from ${from} to ${to}${held ? ` and keep ${held} in ${from}` : ''}?`
      : `Pass out ${keep.size} student${keep.size === 1 ? '' : 's'} of ${from} as alumni${held ? ` and keep ${held} in ${from}` : ''}?`;
    if (!window.confirm(text)) return;
    setBusy(true); setMsg('');
    try {
      const r = await promoteClass({
        from_class_id: Number(fromId), to_class_id: toId === 'passout' ? null : Number(toId),
        detained_ids: data.students.filter(s => !keep.has(s.id)).map(s => s.id), keep_roll_numbers: keepRoll,
      });
      setMsg(r.to
        ? `${r.promoted} moved from ${r.from} to ${r.to}${r.detained ? `, ${r.detained} kept in ${r.from}` : ''} (${r.session}) ✅`
        : `${r.passed_out} passed out of ${r.from} as alumni${r.detained ? `, ${r.detained} kept in ${r.from}` : ''} (${r.session}) ✅`);
      setToId(''); loadClass(); setAlumni(null);
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  async function showAlumni() {
    try { setAlumni([...(await getStudentsByStatus('alumni')), ...(await getStudentsByStatus('left'))]); } catch (err) { setMsg(errText(err)); }
  }

  const current = sessions.find(s => s.is_current);
  const toggle = (id) => { const n = new Set(keep); n.has(id) ? n.delete(id) : n.add(id); setKeep(n); };

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Academic Year &amp; Promotion</h2>
      <Msg msg={msg} className="mb-3" />

      <div className="grid grid-cols-[minmax(280px,360px)_1fr] gap-5 items-start max-[1000px]:grid-cols-1">
        <Card>
          <CardHeader title="Academic years" action={!form && <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1" onClick={() => setForm(suggestSession(sessions))}><Plus size={13} /> New year</Button>} />
          {form && (
            <form onSubmit={saveSession} className="p-4 border-b border-line-soft flex flex-col gap-3 bg-paper">
              <Field label="Name"><input required className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Starts"><input required type="date" className={inputCls} value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
                <Field label="Ends"><input required type="date" className={inputCls} value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink">
                <input type="checkbox" checked={form.make_current} onChange={e => setForm({ ...form, make_current: e.target.checked })} className="accent-[var(--transit)]" /> Make this the current year
              </label>
              <div className="flex gap-2"><Button type="submit">Save</Button>{sessions.length > 0 && <Button type="button" variant="secondary" onClick={() => setForm(null)}>Cancel</Button>}</div>
            </form>
          )}
          {sessions.map(s => (
            <div key={s.id} className="flex items-center gap-3 px-5 py-3 border-t border-line-soft first:border-t-0">
              <div className="flex-1">
                <b className="text-ink">{s.name}</b>
                <span className="block text-xs text-ink-soft">{fmtDate(s.start_date)} – {fmtDate(s.end_date)}{s.promotions ? ` · ${s.promotions} promotion records` : ''}</span>
              </div>
              {s.is_current ? <Badge tone="success">Current</Badge> : <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => makeCurrent(s)}>Make current</Button>}
            </div>
          ))}
          {!sessions.length && !form && <p className="px-5 py-5 text-sm text-ink-soft">No academic year yet.</p>}
        </Card>

        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <h3 className="font-display font-bold text-[15px] text-ink mb-1">Promote a class {current && <span className="text-ink-soft font-normal text-sm">— records go into {current.name}</span>}</h3>
            <p className="text-xs text-ink-soft mb-3">Tip: start with the highest class (pass them out first), then work down, so each class moves into an empty one.</p>
            {!current && <p className="text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit">Create the current academic year first (left).</p>}
            {current && (
              <div className="flex flex-wrap items-end gap-3">
                <Field label="From class">
                  <select className={inputCls} value={fromId} onChange={e => { setFromId(e.target.value); setToId(''); }}>
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                <ArrowRight className="mb-2.5 text-ink-soft" size={18} />
                <Field label="To">
                  <select className={inputCls} value={toId} onChange={e => setToId(e.target.value)} disabled={!fromId}>
                    <option value="">Select</option>
                    {classes.filter(c => String(c.id) !== String(fromId)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="passout">🎓 Pass out (alumni)</option>
                  </select>
                </Field>
                <label className="flex items-center gap-2 text-sm font-semibold text-ink mb-2">
                  <input type="checkbox" checked={keepRoll} onChange={e => setKeepRoll(e.target.checked)} disabled={toId === 'passout'} className="accent-[var(--transit)]" /> Keep roll numbers
                </label>
                <Button onClick={promote} disabled={busy || !toId || !data?.students.length || data?.already_promoted > 0} className="ml-auto flex items-center gap-1.5">
                  {toId === 'passout' ? <GraduationCap size={14} /> : <Star size={14} />} {busy ? 'Working…' : toId === 'passout' ? 'Pass out' : 'Promote'}
                </Button>
              </div>
            )}
          </Card>

          {data && (
            <Card>
              <CardHeader title={`${data.students.length} student${data.students.length === 1 ? '' : 's'} · ${keep.size} moving, ${data.students.length - keep.size} held back`} action={
                <div className="flex gap-2 text-xs font-bold">
                  <button className="text-transit-deep hover:underline" onClick={() => setKeep(new Set(data.students.map(s => s.id)))}>All</button>
                  <button className="text-transit-deep hover:underline" onClick={() => setKeep(new Set())}>None</button>
                </div>
              } />
              {data.already_promoted > 0 && <p className="mx-5 mt-3 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit">This class was already promoted in {data.current_session?.name}.</p>}
              <table className="w-full text-sm">
                <thead><tr className={theadRowCls}><th className="px-4 py-2.5 w-24">Promote</th><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Roll</th><th className="px-4 py-2.5">Adm. no.</th></tr></thead>
                <tbody>
                  {data.students.map(s => (
                    <tr key={s.id} className={`border-t border-line-soft ${keep.has(s.id) ? '' : 'bg-caution-tint'}`}>
                      <td className="px-4 py-2.5"><input type="checkbox" checked={keep.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Promote ${s.name}`} className="w-4 h-4 accent-[var(--transit)]" /></td>
                      <td className="px-4 py-2.5 font-semibold">{s.name}{!keep.has(s.id) && <span className="ml-2 text-xs font-bold text-caution">held back</span>}</td>
                      <td className="px-4 py-2.5 tabular-nums">{s.roll_number || '—'}</td>
                      <td className="px-4 py-2.5">{s.admission_no || '—'}</td>
                    </tr>
                  ))}
                  {!data.students.length && <EmptyRow cols={4}>No students in this class</EmptyRow>}
                </tbody>
              </table>
            </Card>
          )}

          <Card>
            <CardHeader title="Alumni & students who left" action={!alumni && <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1" onClick={showAlumni}><Users size={13} /> Show</Button>} />
            {alumni && (
              <table className="w-full text-sm">
                <tbody>
                  {alumni.map(s => (
                    <tr key={s.id} className="border-t border-line-soft first:border-t-0">
                      <td className="px-4 py-2.5"><button onClick={() => onOpenStudent(s.id)} className="font-semibold text-transit-deep hover:underline">{s.name}</button></td>
                      <td className="px-4 py-2.5"><Badge tone={s.status === 'alumni' ? 'info' : 'neutral'}>{s.status === 'alumni' ? 'Alumni' : 'Left'}</Badge></td>
                      <td className="px-4 py-2.5 text-ink-soft">{s.date_of_leaving ? fmtDate(s.date_of_leaving) : ''}</td>
                    </tr>
                  ))}
                  {!alumni.length && <EmptyRow cols={3}>None yet</EmptyRow>}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
